import { t } from 'i18next';
import z from 'zod';
import { optionalNumericString } from '../../../lib/schemas/numeric-string';
import { CUSTOMER_GROUP_NAME_MAX_LENGTH } from '../constants';

export const QUICK_SETUP_PAYMENT_METHODS = [
  'payment_by_invoice',
  'payment_on_account',
  'upfront_payment',
] as const;

export const QUICK_SETUP_MIN_ORDER_REQUIREMENTS = ['value_based_min', 'order_based_min'] as const;

export const customerGroupQuickSetupSchema = z
  .object({
    baseCurrency: z.string(),
    name: z
      .string()
      .min(1)
      .max(
        CUSTOMER_GROUP_NAME_MAX_LENGTH,
        t('errors:forms.string.max', { max: CUSTOMER_GROUP_NAME_MAX_LENGTH }),
      ),
    assignedPriceList: z.string().min(1),
    paymentMethods: z.array(z.enum(QUICK_SETUP_PAYMENT_METHODS)).nonempty(),
    minOrderRequirements: z.array(z.enum(QUICK_SETUP_MIN_ORDER_REQUIREMENTS)),
    minOrderValue: optionalNumericString({ emptyAsUndefined: true, isWhole: true, min: 0 }),
    minOrderUnits: optionalNumericString({ emptyAsUndefined: true, isWhole: true, min: 0 }),
  })
  .superRefine((data, ctx) => {
    if (data.minOrderRequirements.includes('value_based_min') && data.minOrderValue === undefined) {
      ctx.addIssue({
        code: 'custom',
        message: t('errors:forms.required'),
        path: ['minOrderValue'],
      });
    }
    if (data.minOrderRequirements.includes('order_based_min') && data.minOrderUnits === undefined) {
      ctx.addIssue({
        code: 'custom',
        message: t('errors:forms.required'),
        path: ['minOrderUnits'],
      });
    }
  })
  .transform((data) => ({
    name: data.name,
    settings: {
      priceListSelection: [{ price_list_slug: data.assignedPriceList }],
      allowedPaymentMethods: {
        upfront_payment: data.paymentMethods.includes('upfront_payment'),
        payment_on_account: data.paymentMethods.includes('payment_on_account'),
        payment_by_invoice: data.paymentMethods.includes('payment_by_invoice'),
        quote: false,
      },
      ...(data.minOrderRequirements.includes('value_based_min') && data.minOrderValue !== undefined
        ? {
            orderTotalsValidation: [
              {
                currency_code: data.baseCurrency,
                minimum_value: data.minOrderValue,
                maximum_value: undefined,
                compare_type: 'net',
              },
            ],
          }
        : {}),
      ...(data.minOrderRequirements.includes('order_based_min') && data.minOrderUnits !== undefined
        ? {
            orderItemQuantityValidation: {
              minimum_quantity: data.minOrderUnits,
              maximum_quantity: undefined,
            },
          }
        : {}),
    },
  }));

export type CustomerGroupQuickSetupState = z.input<typeof customerGroupQuickSetupSchema>;
export type CustomerGroupQuickSetupValidated = z.output<typeof customerGroupQuickSetupSchema>;
